import { ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, CredentialsValidationError, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { ListPendingParams } from '../../../interfaces/exam-collect/pending-exam-collection';
import BaseController from '../../BaseController';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/message-service';
import { ExamCollectionWithoutBarcodeHandler } from '../pending-exam-collection/pending-exam-collection-handlers/exam-collection-without-barcode-handler';
import PendingExamCollectionToastHandler from '../pending-exam-collection/pending-exam-collection-handlers/pending-exam-collection-toast-handler';
import { ExamCollectionUtils } from '../../../utils/exam-collection-utils';
import { ToastService } from '@philips-internal/oswald-ui-components';

@Controller({ fragment: 'confirm-exam-collection-unlabelled' })
class ComfirmExamCollectionUnlabelledController extends BaseController {

  @OnMount({ target: 'panel-confirm-exam-collection' })
  onMountLayout(): void {
    this.context.set({shouldDismiss: true});

    const promptConfig = {
      onDismiss: this.onDismiss,
      onError: this.createOnCredentialValidationErrorCallback(),
      onSuccess: this.createOnCredentialValidationSuccessCallback(),
      validateButtonExpression: MessageService.translate('$expression.$702951')
    }

    foundationContext.promptCredentialsValidation(promptConfig);
  }

  createOnCredentialValidationSuccessCallback(): () => Promise<void> {
    const encounterId = this.getHistoryRouteParam<number>('encounterId');
    const prescriptionId = this.getHistoryRouteParam<number>('prescriptionId');

    return (): Promise<void> => {
      this.context.set({shouldDismiss: false});
      return ExamCollectionWithoutBarcodeHandler.createUpdateExamsBody(encounterId, prescriptionId)
        .then(body => {
          return HttpService.post(Endpoint.UPDATE_STATUS_PRESCRIPRION, {}, { body })
            .then(updateStatusResponse => {
              if (updateStatusResponse.status === 200) {
                ExamCollectionWithoutBarcodeHandler.clearSelectedExams();
                this.showToast();
              }
              this.redirect(encounterId, prescriptionId);
              return;
            })
        })
        .catch(() => {
          const exams = ExamCollectionWithoutBarcodeHandler.getSelectedExams();
          const examsDescriptions = exams.map(exam=>exam.nmExame) as string[];
          PendingExamCollectionToastHandler.showExamCollectionFailErrorToast(examsDescriptions, 'S');
          this.redirect(encounterId, prescriptionId);
        })
    }
  }

  createOnCredentialValidationErrorCallback(): (event: CredentialsValidationError) => void {
    let remainingTries = 3;

    return (event: CredentialsValidationError): void => {
      const { abort } = event;

      ToastService.open({
        dismissible: true,
        highlight: false,
        id: 'features.foundation-context.nok-toast',
        level: ToastLevel.ERROR,
        message: 'login-message.invalid-user-password-text',
        showIcon: true,
        timeout: 5000,
        type: ToastType.BANNER,
      });

      if (remainingTries > 0) {
        remainingTries = remainingTries - 1;
      }

      if (remainingTries === 0) {
        applicationContext.popNavigationItem();
        applicationContext.popNavigationItem();
        abort();
      }
    };
  }

  redirect(encounterNumber: string | number, prescriptionNumber: string | number): void {
    HttpService.get(Endpoint.EXAM_COLLECT_LIST_PENDING, {
      encounterNumber,
      limit: 1,
      page: 0,
      prescriptionNumber
    }).then(response => {
      const body = response.body as ListPendingParams[];
      if (body.length > 0) {
        applicationContext.popNavigationItem();
      } else {
        this.redirectIfHasNotExams();
      }
    });
  }

  async redirectIfHasNotExams(): Promise<void> {
    const encounterId = this.getHistoryRouteParam<number>('encounterId');
    const exist = await ExamCollectionUtils.existsMedicalPrescriptions(encounterId);

    if (exist) {
      applicationContext.popNavigationItem();
      applicationContext.popNavigationItem();
    } else {
      applicationContext.clearNavigationStack();
      this.historyService.push(
        `${ApplicationRoute.EXAM_COLLECTION}${ApplicationRoute.SEARCH_PATIENT}`
      );
    }
  }

  showToast(): void {
    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'features.foundation-context.ok-toast',
      level: ToastLevel.SUCCESS,
      message: 'pending.exam-collection-stats',
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  onDismiss = (): void => {
    const shouldDismiss = this.context.get('shouldDismiss');
    if(shouldDismiss) {
      applicationContext.popNavigationItem();
    }
  };
}

export default ComfirmExamCollectionUnlabelledController;